import { formatPrice } from '@/utils/tools';

/** 任务总价展示精度（code 对应截断单位 10^code 元） */
export enum TotalAmountPrecision {
  /** 个位，万元 4 位小数，截断至 1 元 */
  ONES = 0,
  /** 十位，万元 3 位小数，截断至 10 元 */
  TENS = 1,
  /** 百位，万元 2 位小数，截断至 100 元 */
  HUNDREDS = 2,
  /** 千位，万元 1 位小数，截断至 1,000 元 */
  THOUSANDS = 3,
  /** 万位，万元 0 位小数，截断至 10,000 元 */
  TEN_THOUSANDS = 4,
  /** 十万位，万元 0 位小数，截断至 100,000 元 */
  HUNDRED_THOUSANDS = 5,
  /** 百万位，万元 0 位小数，截断至 1,000,000 元 */
  MILLIONS = 6,
}

/** 任务总价展示精度默认值：万位（截断至 10,000 元） */
export const DEFAULT_TOTAL_AMOUNT_PRECISION = TotalAmountPrecision.TEN_THOUSANDS;

export const TotalAmountPrecisionNames: Record<TotalAmountPrecision, string> = {
  [TotalAmountPrecision.ONES]: '个位',
  [TotalAmountPrecision.TENS]: '十位',
  [TotalAmountPrecision.HUNDREDS]: '百位',
  [TotalAmountPrecision.THOUSANDS]: '千位',
  [TotalAmountPrecision.TEN_THOUSANDS]: '万位',
  [TotalAmountPrecision.HUNDRED_THOUSANDS]: '十万位',
  [TotalAmountPrecision.MILLIONS]: '百万位',
};

export const TotalAmountPrecisionOptions = Object.values(TotalAmountPrecision)
  .filter((v): v is TotalAmountPrecision => typeof v === 'number')
  .map((value) => ({
    value,
    label: TotalAmountPrecisionNames[value],
  }));

/** 截断单位（元）= 10^code */
export function getTruncateUnitYuan(precisionCode: number): number {
  return Math.pow(10, precisionCode);
}

/** 万元小数位 */
export function getWanYuanDecimalPlaces(precisionCode: number): number {
  return Math.max(0, 4 - precisionCode);
}

/** 按精度向下截断金额（元，不舍入） */
export function truncateAmountDownYuan(amountYuan: number, precisionCode: number): number {
  const unit = getTruncateUnitYuan(precisionCode);
  return Math.floor(amountYuan / unit) * unit;
}

/** 与比价测算页 effectiveReviewTotalPrice 一致：按任务总价精度截断复核总价 */
export function resolveEffectiveReviewTotalYuan(
  amountYuan: number | undefined | null,
  precisionCode: number = DEFAULT_TOTAL_AMOUNT_PRECISION,
): number | undefined {
  if (amountYuan === undefined || amountYuan === null || Number.isNaN(amountYuan) || amountYuan <= 0) {
    return undefined;
  }
  const truncated = truncateAmountDownYuan(amountYuan, precisionCode);
  return truncated > 0 ? truncated : undefined;
}

/** 复核总价占位符：有源值时返回截断后的格式化字符串，否则返回默认文案 */
export function buildReviewTotalPlaceholder(
  sourceYuan: number | undefined,
  precisionCode: number,
  defaultText: string,
): string {
  if (sourceYuan === undefined || Number.isNaN(sourceYuan)) {
    return defaultText;
  }
  const truncated = truncateAmountDownYuan(sourceYuan, precisionCode);
  const formatted = formatPrice(truncated, 0);
  return formatted === '-' ? defaultText : formatted;
}
